import { nextTick } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import MainFlow from '@/views/MainFlow.vue';
import LocaleLayout from '@/views/LocaleLayout.vue';
import i18n, { supportedLocales } from '@/i18n';
import type { SupportedLocale } from '@/i18n';
import { trackPageview } from '@/utils/analytics';

/**
 * Application router (spec 047-blog-section, Task Group 0;
 * spec 045-multilingual-support, Phase 7 — Locale-Prefixed URL Routing).
 *
 * The app historically navigated through a Pinia step-machine
 * (`store.currentStep`) rendered by `v-if` blocks in `App.vue`. Task Group 0
 * introduced `vue-router`; spec 045 Phase 7 (WE-1) now nests EVERY route under a
 * mandatory `/:locale` prefix.
 *
 * ALWAYS-PREFIX (product-owner decision): there are NO unprefixed app routes —
 * the locale prefix is the single source of truth for the active UI locale,
 * INCLUDING the default `en`. The bare path `/` and any legacy unprefixed URL
 * redirect (replace) to the resolved locale prefix (`beforeEach` guard below).
 * Resolution order: persisted `localStorage[cpt-locale]` → best
 * `navigator.language` match → `'en'` (FR-010, FR-011).
 *
 * Migration decision (spec 047 v2, §0.1): the payment / OAuth / report / support
 * journey stays **store-driven inside a routed view** (`MainFlow.vue`). The
 * wizard's transitions are non-URL state, so the store remains their source of
 * truth; the router only owns URL-addressable surfaces.
 */

// The `:locale` param is constrained to the live `supportedLocales` list so an
// unsupported first segment never matches the prefixed tree (it falls to the
// top-level catch-all and is redirected by the guard). Built from the i18n
// source of truth so adding a locale there automatically extends the route.
const LOCALE_RE = supportedLocales.join('|');

// Children of the `/:locale` parent. Paths are RELATIVE to the prefix; the
// catch-all renders the store-driven shell so legacy entry URLs (Stripe return,
// Tesla OAuth callbacks) still resolve to `App.vue`'s mount handlers.
const localeChildren: RouteRecordRaw[] = [
  {
    path: '',
    name: 'home',
    component: MainFlow,
  },
  // Blog routes. Registered ahead of the catch-all so they take precedence;
  // lazy-loaded so the blog modules (incl. `marked`) ship as their own chunks.
  {
    path: 'blog',
    name: 'blog-index',
    component: () => import('@/views/BlogIndex.vue'),
  },
  {
    // `:timestamp` is the 12-digit YYYYMMDDHHmm publish stamp (FR-004). FR-016:
    // the locale lives in the `/:locale` prefix, so the post URL drops the old
    // duplicate in-path lang → `/<locale>/blog/<ts>/<slug>`.
    path: 'blog/:timestamp(\\d{12})/:slug',
    name: 'blog-post',
    component: () => import('@/views/BlogPost.vue'),
  },
  {
    // Legacy 301 (FR-016): the pre-migration `…/blog/<ts>/<lang>/<slug>` shape —
    // whether hit directly (the unprefixed-redirect guard prepends a resolved
    // locale first) or as an already-prefixed duplicate — redirects to the
    // canonical `/<lang>/blog/<ts>/<slug>`. The IN-PATH `:lang` becomes the
    // prefix (it wins over the outer/resolved locale) so an indexed link in any
    // language lands on that language's post, preserving the SEO signal.
    //
    // `:lang` is constrained to supportedLocales so this 4-segment record only
    // fires for the genuine legacy shape — it must NOT swallow the canonical
    // 3-segment post URL plus a prerender artifact (`…/<slug>/index.html`),
    // whose third segment is a slug, not a locale.
    path: `blog/:timestamp(\\d{12})/:lang(${LOCALE_RE})/:slug`,
    name: 'blog-post-legacy',
    redirect: (to) => {
      const { lang, timestamp, slug } = to.params;
      return `/${lang}/blog/${timestamp}/${slug}`;
    },
  },
  // Data-transparency page (spec 049-google-analytics, Phase 6).
  {
    path: 'data-and-privacy',
    name: 'data-and-privacy',
    component: () => import('@/views/DataAndPrivacy.vue'),
  },
  // Contact page — a non-step, URL-addressable route.
  {
    path: 'contact',
    name: 'contact',
    component: () => import('@/views/Contact.vue'),
  },
  // About Us page — describes Boxinom and Csitea.
  {
    path: 'about',
    name: 'about',
    component: () => import('@/views/AboutUs.vue'),
  },
  // Unknown path UNDER a valid locale prefix → store-driven shell (landing).
  {
    path: ':pathMatch(.*)*',
    name: 'catch-all',
    component: MainFlow,
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: `/:locale(${LOCALE_RE})`,
    component: LocaleLayout,
    children: localeChildren,
  },
  // Everything else has no valid locale prefix (bare `/`, `/blog`, `/foo`, …).
  // The guard redirects it to the resolved-locale prefixed equivalent before it
  // renders; this component is only a never-rendered fallback.
  {
    path: '/:pathMatch(.*)*',
    name: 'unprefixed',
    component: MainFlow,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Resolve the locale to use for an unprefixed entry URL (FR-011).
 * Order: persisted `localStorage[cpt-locale]` → best `navigator.language`
 * match → `'en'`.
 */
function resolveLocale(): SupportedLocale {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('cpt-locale');
    if (saved && (supportedLocales as string[]).includes(saved)) {
      return saved as SupportedLocale;
    }
    const navs =
      typeof navigator !== 'undefined'
        ? navigator.languages && navigator.languages.length
          ? navigator.languages
          : navigator.language
            ? [navigator.language]
            : []
        : [];
    for (const nav of navs) {
      if (!nav) continue;
      const lower = nav.toLowerCase();
      if ((supportedLocales as string[]).includes(lower)) return lower as SupportedLocale;
      const primary = lower.split('-')[0];
      if ((supportedLocales as string[]).includes(primary)) return primary as SupportedLocale;
    }
  }
  return 'en';
}

/**
 * Apply a valid prefix as the active locale (FR-012, FR-017): set the i18n
 * locale, persist it for the next entry, and mirror the document direction for
 * RTL (Hebrew). App.vue also watches `locale` for `dir`, but setting it here
 * keeps the prefix authoritative even before the app mounts.
 */
function activateLocale(locale: SupportedLocale): void {
  // `legacy: false` makes `i18n.global.locale` a WritableComputedRef at runtime,
  // but the createI18n overload widens its static type to the bare Locale union
  // (the legacy shape). Cast to the real ref shape to read/write `.value`.
  const globalLocale = i18n.global.locale as unknown as { value: SupportedLocale };
  if (globalLocale.value !== locale) {
    globalLocale.value = locale;
  }
  if (typeof window !== 'undefined') {
    localStorage.setItem('cpt-locale', locale);
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }
}

// Canonicalize static-hosting artifacts so deep-linked / crawler-followed
// prerender URLs resolve to real routes. The SEO pipeline emits each blog post
// as a static `…/index.html`; GCS serves the directory form and 301-redirects
// the bare canonical to the trailing-slash form. Strip a trailing `/index.html`
// and any trailing slash so the deep URL resolves to the live route on cold
// load instead of falling through and wiping the prerendered article.
router.beforeEach((to) => {
  let path = to.path;
  if (path.endsWith('/index.html')) path = path.slice(0, -'/index.html'.length) || '/';
  if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');
  if (path !== to.path) {
    return { path, query: to.query, hash: to.hash, replace: true };
  }
});

// Locale-prefix guard (spec 045 Phase 7, FR-010…FR-012, FR-017).
// - Valid locale prefix → activate it (set i18n + persist + RTL) and proceed.
// - No / unsupported prefix (incl. bare `/`) → redirect (replace) to the
//   resolved-locale prefix, preserving the rest of the path + query + hash.
router.beforeEach((to) => {
  const first = to.path.split('/')[1] ?? '';
  if ((supportedLocales as string[]).includes(first)) {
    activateLocale(first as SupportedLocale);
    return;
  }
  const resolved = resolveLocale();
  const rest = to.path === '/' ? '' : to.path;
  return { path: `/${resolved}${rest}`, query: to.query, hash: to.hash, replace: true };
});

// GA4 page-view tracking (spec 049-google-analytics, Task 3.1). Fires after each
// navigation; `nextTick` lets the routed view update `document.title` (via
// @unhead/vue) before we read it. `trackPageview` is a no-op until the user has
// accepted cookies and GA is active, so this is GDPR-safe by construction.
router.afterEach((to) => {
  nextTick(() => {
    trackPageview(to.fullPath, document.title || to.name?.toString() || '');
  });
});

export default router;
